/* eslint-disable react/display-name */
import React, { useMemo } from "react"
import { useSelector } from "react-redux"
import { Tabs, Tab } from "shared/tabs"
import { loading as categoryDataLoading } from "reduxSlices/categorySlice"
import { isFeatureEnabled } from "reduxSlices/featuresSlice"
import {
  loading as formSubmissionDataLoading,
  submissionPermissions as submissionPermissionsSelector,
  tracked,
} from "reduxSlices/formSubmissionSlice"
import { initializing as conversationsInitializing } from "reduxSlices/formSubmissionConversationsSlice"
import { loading as groupsDataLoading } from "reduxSlices/groupsSlice"
import { loading as usersDataLoading } from "reduxSlices/organizationUsersSlice"
import { CONVERSATIONS } from "utils/featuresHelpers"
import { tabSlugFromQueryParams } from "utils/tabHelper"
import {
  FORM_SUBMISSION_ACTIVITIES_TAB_SLUG,
  FORM_SUBMISSION_ATTACHMENTS_TAB_SLUG,
  FORM_SUBMISSION_AUDIT_TRAIL_TAB_SLUG,
  FORM_SUBMISSION_CONVERSATIONS_TAB_SLUG,
  FORM_SUBMISSION_SUBMISSION_ACCESS_TAB_SLUG,
  FORM_SUBMISSION_SUBMISSION_TAB_SLUG,
  FORM_SUBMISSION_SUPPLEMENTAL_TAB_SLUG,
} from "utils/tabSlugHelpers"
import LoadingIndicator from "shared/LoadingIndicator"
import ActivitiesContent, { ACTIVITIES_TAB_ID, ACTIVITIES_TAB_PANEL_ID } from "./ActivitiesContent"
import SubmissionContent, { SUBMISSION_CONTENT_TAB_ID, SUBMISSION_CONTENT_TAB_PANEL_ID } from "./SubmissionContent"
import SubmissionAccess, { ACCESS_TAB_ID, ACCESS_TAB_PANEL_ID } from "./SubmissionAccess"
import AuditTrailContent, { AUDIT_TRAIL_TAB_ID, AUDIT_TRAIL_TAB_PANEL_ID } from "./AuditTrailContent"
import AttachmentsContent, { ATTACHMENTS_TAB_ID, ATTACHMENTS_TAB_PANEL_ID } from "./AttachmentsContent"
import AttachmentsCountBadge from "./AttachmentsContent/Content/AttachmentsCountBadge"
import ConversationsContent, { CONVERSATIONS_TAB_ID, CONVERSATIONS_TAB_PANEL_ID } from "./ConversationsContent"
import SupplementalCountBadge from "./SupplementalContent/RelatedFormSubmissions/SupplementalCountBadge"
import SupplementalContent, { SUPPLEMENTAL_TAB_ID, SUPPLEMENTAL_TAB_PANEL_ID } from "./SupplementalContent"

export const SUBMISSION_TAB = {
  title: "Submission",
  slug: FORM_SUBMISSION_SUBMISSION_TAB_SLUG,
  TabContent: SubmissionContent,
  tabId: SUBMISSION_CONTENT_TAB_ID,
  tabPanelId: SUBMISSION_CONTENT_TAB_PANEL_ID,
}

export const ACTIVITIES_TAB = {
  title: "Activities",
  slug: FORM_SUBMISSION_ACTIVITIES_TAB_SLUG,
  TabContent: ActivitiesContent,
  tabId: ACTIVITIES_TAB_ID,
  tabPanelId: ACTIVITIES_TAB_PANEL_ID,
}

export const SUBMISSION_ACCESS_TAB = {
  title: "Submission Access",
  slug: FORM_SUBMISSION_SUBMISSION_ACCESS_TAB_SLUG,
  TabContent: SubmissionAccess,
  tabId: ACCESS_TAB_ID,
  tabPanelId: ACCESS_TAB_PANEL_ID,
}

export const ATTACHMENTS_TAB = {
  title: "Attachments",
  slug: FORM_SUBMISSION_ATTACHMENTS_TAB_SLUG,
  NotificationBadge: AttachmentsCountBadge,
  TabContent: AttachmentsContent,
  tabId: ATTACHMENTS_TAB_ID,
  tabPanelId: ATTACHMENTS_TAB_PANEL_ID,
}

export const SUPPLEMENTAL_TAB = {
  title: "Supplemental",
  slug: FORM_SUBMISSION_SUPPLEMENTAL_TAB_SLUG,
  NotificationBadge: SupplementalCountBadge,
  TabContent: SupplementalContent,
  tabId: SUPPLEMENTAL_TAB_ID,
  tabPanelId: SUPPLEMENTAL_TAB_PANEL_ID,
}

export const AUDIT_TRAIL_TAB = {
  title: "Audit Trail",
  slug: FORM_SUBMISSION_AUDIT_TRAIL_TAB_SLUG,
  TabContent: AuditTrailContent,
  tabId: AUDIT_TRAIL_TAB_ID,
  tabPanelId: AUDIT_TRAIL_TAB_PANEL_ID,
}

const CONVERSATIONS_TAB = {
  title: "Conversations",
  slug: FORM_SUBMISSION_CONVERSATIONS_TAB_SLUG,
  TabContent: ConversationsContent,
  tabId: CONVERSATIONS_TAB_ID,
  tabPanelId: CONVERSATIONS_TAB_PANEL_ID,
}

const DEFAULT_FORM_SUBMISSION_TABS = [
  SUBMISSION_TAB,
  ACTIVITIES_TAB,
  SUPPLEMENTAL_TAB,
]

// Tab order:
// 1. Submission
// 2. Activities
// 3. Conversations (if visible)
// 4. Submission Access (if visible)
// 5. Attachments (if visible)
// 6. Supplemental
// 7. Audit Trail (if Visible)
//
// The Submission and Activities tabs are always present at the beginning of the tabs list.
//
// The Supplemental tab is also always visible, but it's position in the list depends on which
// optional tabs are included.
//
// The optional tabs (except for the Audit Trail tab) are spliced into the list between
// the Activities and Supplemental tabs.
//
// If the options Audit Trail tab is included, it's at the end of the tabs list.
const listVisibleTabs = ({
  includeAuditTrailTab, includeConversationsTab, includeAttachmentsTab, includeSubmissionAccessTab,
}) => {
  const tabs = [...DEFAULT_FORM_SUBMISSION_TABS]

  if (includeAttachmentsTab) tabs.splice(2, 0, ATTACHMENTS_TAB)
  if (includeSubmissionAccessTab) tabs.splice(2, 0, SUBMISSION_ACCESS_TAB)
  if (includeConversationsTab) tabs.splice(2, 0, CONVERSATIONS_TAB)
  if (includeAuditTrailTab) tabs.push(AUDIT_TRAIL_TAB)

  return tabs
}

const dataLoadingSelector = (state) => (
  categoryDataLoading(state)
  || conversationsInitializing(state)
  || formSubmissionDataLoading(state)
  || groupsDataLoading(state)
  || usersDataLoading(state)
)

const FormSubmissionTabs = () => {
  const conversationsFeatureEnabled = useSelector(isFeatureEnabled(CONVERSATIONS))
  const formSubmissionIsTracked = useSelector(tracked)
  const submissionPermissions = useSelector(submissionPermissionsSelector)
  const waitForDataToLoad = useSelector(dataLoadingSelector)

  const visibleTabs = useMemo(() => (
    listVisibleTabs({
      includeAuditTrailTab: submissionPermissions.canViewAuditTrail,
      includeConversationsTab: conversationsFeatureEnabled && formSubmissionIsTracked,
      includeAttachmentsTab: submissionPermissions.canViewUploadDownloadAttachments,
      includeSubmissionAccessTab: submissionPermissions.canManageSubmissionGrants,
    })
  ), [conversationsFeatureEnabled, formSubmissionIsTracked, submissionPermissions])

  return (
    <Tabs
      initialActiveSlug={tabSlugFromQueryParams()}
    >
      {
        visibleTabs.map(({
          title, slug, TabContent, NotificationBadge, tabId, tabPanelId,
        }) => (
          <Tab
            key={title}
            title={title}
            slug={slug}
            NotificationBadge={NotificationBadge}
            tabId={tabId}
            tabPanelId={tabPanelId}
          >
            {
              waitForDataToLoad
                ? (
                  <div className="inner-container">
                    <LoadingIndicator textValue="Loading form submission data ..." />
                  </div>
                )
                : <TabContent />
            }
          </Tab>
        ))
      }
    </Tabs>

  )
}

export default FormSubmissionTabs
